import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

/** Body for POST `/api/ringover_calls` — extend to match your API contract. */
export interface RingoverCallCreateParams {
  readonly user_id: number | string;
  readonly client_phone: string;
}

/** Response of POST `/api/ringover_calls`. */
export interface RingoverCallStartResponse {
  readonly success: boolean;
  readonly call_id: number | string;
  readonly channel_id: number | string;
}

/**
 * Known statuses returned by the polling endpoint. The string list is open on purpose
 * (the back may add new states); the dialog only compares against `'answered'`.
 */
export type RingoverCallStatus = "ringing" | "answered" | "hangup" | "missed" | "voicemail" | string;

/** Response of GET `/api/ringover_calls/:call_id/status`. */
export interface RingoverCallStatusResponse {
  readonly call_id: number | string;
  readonly status: RingoverCallStatus;
}

/** Response of GET `/api/ringover_calls/ringover_phone_number`. */
export interface RingoverPhoneNumberResponse {
  readonly success: boolean;
  readonly ringover_phone_number: string;
}

/**
 * Root injector so dialogs opened from lazy routes / UiKitModule (without ServicesModule)
 * still resolve this service. HttpClientService is also root.
 */
@Injectable({
  providedIn: "root",
})
export class RingoverCallsService {
  constructor(private http: HttpClientService) {}

  public startRingoverCall(params: RingoverCallCreateParams): Observable<RingoverCallStartResponse> {
    const body = {
      user_id: params.user_id,
      client_phone: params.client_phone,
    };

    return this.http
      .post(`/api/ringover_calls`, body)
      .pipe(
        tap((_) => console.log("RingoverCallsService.startRingoverCall: Ok"))
      ) as Observable<RingoverCallStartResponse>;
  }

  /**
   * Polls the current status of a Ringover call. The webhook on the back updates this
   * record; the front polls until the status reaches a terminal value (e.g. `answered`).
   */
  public getRingoverCallStatus(callId: number | string): Observable<RingoverCallStatusResponse> {
    const params = {
      call_id: callId.toString(),
    };

    return this.http
      .get(`/api/ringover_calls/status`, { params })
      .pipe(
        tap((_) => console.log("RingoverCallsService.getRingoverCallStatus: Ok"))
      ) as Observable<RingoverCallStatusResponse>;
  }

  /** Returns the Ringover phone number assigned to the application's internal user (analyst). */
  public getRingoverPhoneNumber(applicationId: number | string): Observable<RingoverPhoneNumberResponse> {
    const params = {
      application_id: applicationId.toString(),
    };

    return this.http
      .get(`/api/ringover_calls/ringover_phone_number`, { params })
      .pipe(
        tap((_) => console.log("RingoverCallsService.getRingoverPhoneNumber: Ok"))
      ) as Observable<RingoverPhoneNumberResponse>;
  }
}
